import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import authService from '../../../services/candidate/authService';

const Topbar = () => {
    const navigate = useNavigate();
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [userData, setUserData] = useState(null);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const dropdownRef = useRef(null);

    // Check auth status on mount and when localStorage changes
    useEffect(() => {
        const checkAuthStatus = () => {
            const isAuth = authService.isAuthenticated();
            setIsLoggedIn(isAuth);
            
            if (isAuth) {
                const userStr = localStorage.getItem('candidate_user');
                if (userStr) {
                    try {
                        setUserData(JSON.parse(userStr));
                    } catch (error) {
                        console.error('Error parsing user data:', error);
                    }
                }
            } else {
                setUserData(null);
            }
        };

        checkAuthStatus();

        // Listen for storage changes (login/logout in other tabs)
        const handleStorageChange = (e) => {
            if (e.key === 'candidate_user' || e.key === 'candidate_token') {
                checkAuthStatus();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowUserMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLogout = () => {
        authService.logout();
        setIsLoggedIn(false);
        setUserData(null);
        setShowUserMenu(false);
        navigate('/');
    };

    const getInitial = () => {
        if (!userData) return 'C';
        return (
            userData.firstName?.charAt(0)?.toUpperCase() ||
            userData.name?.charAt(0)?.toUpperCase() ||
            'C'
        );
    };

    return (
        <nav className="fixed w-full top-0 z-50 bg-[#222629]">
            <div className="container mx-auto px-4 md:px-6 py-4">
                <div className="flex justify-between items-center">
                    {/* Logo */}
                    <div className="flex items-center space-x-3">
                        <Link to="/" className="flex items-center">
                            <img 
                                src="/logo-light.png" 
                                alt="Hunar Pool" 
                                className="h-8 w-auto" 
                                draggable="false"
                            />
                        </Link>
                    </div>
                    
                    {/* Auth Links / User Menu */}
                    <div className="flex items-center space-x-4">
                        {isLoggedIn && userData ? (
                            // Logged in user - Show user menu
                            <div className="relative" ref={dropdownRef}>
                                <button 
                                    onClick={() => setShowUserMenu(!showUserMenu)}
                                    className="flex items-center space-x-2 focus:outline-none"
                                >
                                    {(userData.avatar ? (
                                    <img 
                                        src={userData.avatar} 
                                        alt={userData.name}
                                        className="w-8 h-8 rounded-full"
                                    />
                                    ) :  
                                    <div className="h-8 w-8 rounded-full bg-gradient-to-r from-[#0096C7] to-[#4361EE] hover:from-[#00a8e0] hover:to-[#4a6bf5] flex items-center justify-center text-white font-semibold">
                                        {getInitial()}
                                    </div>
                                    )}

                                    <span className="text-gray-300 font-medium hidden md:block">
                                        {userData.fullName || userData.name || 'User'}
                                    </span>
                                    <i className="fas fa-chevron-down text-gray-400 text-xs"></i>
                                </button>

                                {/* Dropdown Menu */}
                                {showUserMenu && (
                                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 z-50">
                                        <Link 
                                            to="/candidate/dashboard"
                                            onClick={() => setShowUserMenu(false)}
                                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                        >
                                            <i className="fas fa-tachometer-alt mr-2"></i>
                                            Dashboard
                                        </Link>
                                        <Link 
                                            to="/candidate/profile"
                                            onClick={() => setShowUserMenu(false)}
                                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                        >
                                            <i className="fas fa-user mr-2"></i>
                                            My Profile
                                        </Link>
                                        <Link 
                                            to="/candidate/saved-jobs"
                                            onClick={() => setShowUserMenu(false)}
                                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                        >
                                            <i className="fas fa-heart mr-2"></i>
                                            Saved Jobs
                                        </Link>
                                        <div className="border-t border-gray-100 my-1"></div>
                                        <button 
                                            onClick={handleLogout}
                                            className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                        >
                                            <i className="fas fa-sign-out-alt mr-2"></i>
                                            Sign Out
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            // Not logged in - Show Sign In and Find Jobs
                            <>
                                <Link 
                                    to="/candidate/login" 
                                    className="text-gray-300 hover:text-[#0096C7] font-medium text-sm md:text-base transition"
                                >
                                    Sign In
                                </Link>
                                <Link 
                                    to="/jobs" 
                                    className="bg-gradient-to-r from-[#0096C7] to-[#4361EE] text-white px-4 py-2 rounded-lg font-medium hover:shadow-lg transition-all duration-300 text-sm md:text-base hover:shadow-[#0096C7]/20"
                                >
                                    Find Jobs
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </nav>
    );
};

export default Topbar;